import React, {
  useEffect, useMemo, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import Button from 'components/Button/Button';
import Backdrop from 'components/Modal/Backdrop';
import { VALUE_CHAIN_AI_AGENT } from 'constants/general';
import { BUTTON_VARIANT } from 'constants/interfaces';
import { getMonitoringAgentBySessionType } from 'constants/monitoringAgents';
import {
  getAIGeneratingSessionStatus,
  getSessionSelectedUnitLiveStatus,
  getUnitIdFromNodeId,
  normalizeAIGeneratingModalTabDisplayStatus,
} from 'utils/AICreatorHelper';
import { getSessionTemplateTitle } from 'utils/sessionTemplateTitle';

import { isMonitoringSessionType } from '../MonitoringAgent/utils/monitoringSessionFlowUtils';
import AIGeneratingModalHeader from './components/AIGeneratingModalHeader/AIGeneratingModalHeader';
import AIGeneratingModalListItem from './components/AIGeneratingModalListItem/AIGeneratingModalListItem';
import AIGeneratingModalSessionsList from './components/AIGeneratingModalSessionsList/AIGeneratingModalSessionsList';

import type { IAICardSelection } from '../AICardPanel/interfaces/IAICardSelection';
import type {
  IAIGeneratingModalItem,
  IAIGeneratingModalSessionData,
  TAIGeneratingCardStatus,
} from './interfaces/IAIGeneratingModal';
import type IAIGeneratingModal from './interfaces/IAIGeneratingModal';

import styles from './sass/AIGeneratingModal.module.scss';

function AIGeneratingModal({
  open,
  onClose,
  selectedCards,
  session,
  sessions = [],
  onCardClick,
  onSessionClick,
  lastBomSocketPayload,
  titleKey,
  descriptionKey,
  useSessionLevelItemStatus = false,
  sessionsListLayout = 'list',
}: IAIGeneratingModal) {
  const { t } = useTranslation();
  const [activeSessionId, setActiveSessionId] = useState<number | undefined>(undefined);

  const activeSessionData = useMemo<IAIGeneratingModalSessionData | undefined>(
    () => sessions.find(({ session: sessionItem }) => sessionItem.id === activeSessionId),
    [activeSessionId, sessions],
  );

  const getSessionDisplayName = (sessionData: IAIGeneratingModalSessionData): string => {
    const templateTitle = getSessionTemplateTitle(sessionData.session);

    if (templateTitle) {
      return templateTitle;
    }

    if (sessionData.session.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE) {
      const selectedCardNames = sessionData.selectedCards
        .map((card) => card.name)
        .filter(Boolean)
        .join(', ');

      return t('valueChainMap.aiGeneratingBomSessionTitle', { names: selectedCardNames });
    }

    const { type: sessionType } = sessionData.session;
    const monitoringAgent = getMonitoringAgentBySessionType(sessionType);

    if (monitoringAgent) {
      return t(monitoringAgent.titleKey);
    }

    if (sessionType === VALUE_CHAIN_AI_AGENT.SESSION_TYPE) {
      return t('valueChainMap.aiPanelTitle');
    }

    return sessionType || '';
  };

  const currentSession = activeSessionData?.session || session;
  const currentSelectedCards = activeSessionData?.selectedCards || selectedCards;
  const isCarouselLayout = sessionsListLayout === 'carousel';
  const isSessionsOverviewVisible = sessions.length > 0 && !activeSessionData && !isCarouselLayout;

  useEffect(() => {
    if (!open) {
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
      }
      return undefined;
    }
    document.body.style.overflow = 'hidden';
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, open]);

  useEffect(() => {
    if (!open) {
      setActiveSessionId(undefined);
      return;
    }

    if (sessionsListLayout === 'carousel' && sessions[0]) {
      const hasActiveSession = sessions.some(({ session: sessionItem }) => sessionItem.id === activeSessionId);

      if (!hasActiveSession) {
        setActiveSessionId(sessions[0].session.id);
      }

      return;
    }

    if (activeSessionId && !sessions.some(({ session: sessionItem }) => sessionItem.id === activeSessionId)) {
      setActiveSessionId(undefined);
    }
  }, [activeSessionId, open, sessions, sessionsListLayout]);

  const items = useMemo<IAIGeneratingModalItem[]>(() => {
    const selectedUnits = currentSession?.selectedUnits || [];
    const isMonitoringSession = isMonitoringSessionType(currentSession?.type);
    const sessionLevelStatus = useSessionLevelItemStatus || isMonitoringSession
      ? normalizeAIGeneratingModalTabDisplayStatus(currentSession?.status)
      : undefined;
    const itemNameTranslationKey = currentSession?.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
      ? 'valueChainMap.aiGeneratingSearchProductsItem'
      : 'valueChainMap.aiGeneratingSearchSuppliersItem';

    return currentSelectedCards.map((card, index) => {
      const cardUnitId = getUnitIdFromNodeId(card.nodeId);
      const matchedSelectedUnit = selectedUnits.find((selectedUnit, selectedUnitIndex) => {
        if (cardUnitId !== null && selectedUnit.unitId === cardUnitId) {
          return true;
        }

        return selectedUnitIndex === index && selectedUnit.type === card.type;
      });

      return {
        nodeId: card.nodeId,
        name: isMonitoringSession ? card.name : t(itemNameTranslationKey, { name: card.name }),
        status: sessionLevelStatus
          ?? normalizeAIGeneratingModalTabDisplayStatus(
            matchedSelectedUnit
              ? getSessionSelectedUnitLiveStatus(currentSession?.id, matchedSelectedUnit, lastBomSocketPayload)
              : undefined,
          ),
      };
    });
  }, [
    currentSelectedCards,
    currentSession?.id,
    currentSession?.status,
    currentSession?.selectedUnits,
    currentSession?.subType,
    currentSession?.type,
    lastBomSocketPayload,
    t,
    useSessionLevelItemStatus,
  ]);

  const getSessionStatus = (sessionData: IAIGeneratingModalSessionData): TAIGeneratingCardStatus => (
    getAIGeneratingSessionStatus(sessionData.session, lastBomSocketPayload)
  );

  const handleCardClick = (card: IAICardSelection) => {
    if (activeSessionData && onSessionClick) {
      const isMonitoringSession = isMonitoringSessionType(activeSessionData.session.type);
      const isReviewStatus = activeSessionData.session.status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
        || activeSessionData.session.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
      const shouldOpenSessionModal = isMonitoringSession
        || isReviewStatus
        || activeSessionData.session.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
        || activeSessionData.session.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;

      if (shouldOpenSessionModal) {
        onSessionClick(activeSessionData, card);
        return;
      }
    }

    if (!onCardClick) {
      return;
    }

    onCardClick(card);
  };

  const handleSessionOpen = (sessionId: number) => {
    const matchedSessionData = sessions.find(({ session: sessionItem }) => sessionItem.id === sessionId);
    if (!matchedSessionData) {
      return;
    }

    const isMonitoringSession = isMonitoringSessionType(matchedSessionData.session.type);
    const isReviewStatus = matchedSessionData.session.status === VALUE_CHAIN_AI_AGENT.STATUS_REVIEW
      || matchedSessionData.session.status === VALUE_CHAIN_AI_AGENT.COMPLETE_BOM_ITEMS_COUNT;
    const shouldOpenSessionModal = isMonitoringSession
      || isReviewStatus
      || matchedSessionData.session.subType === VALUE_CHAIN_AI_AGENT.BILL_OF_MATERIALS_SESSION_TYPE
      || matchedSessionData.session.subType === VALUE_CHAIN_AI_AGENT.SUPPLIER_DISCOVERY_SESSION_TYPE;

    if (shouldOpenSessionModal && onSessionClick) {
      onSessionClick(matchedSessionData);
      return;
    }

    setActiveSessionId(sessionId);
  };

  const handleBackToSessions = () => {
    setActiveSessionId(undefined);
  };
  if (!open) {
    return null;
  }

  return createPortal(
    <Backdrop>
      <div
        className={styles.root}
        aria-modal="true"
        role="dialog"
        aria-label={t('valueChainMap.aiGeneratingModalTitle')}
      >
        <AIGeneratingModalHeader
          title={t(titleKey || 'valueChainMap.aiGeneratingModalTitle')}
          description={t(descriptionKey || 'valueChainMap.aiGeneratingModalDescription')}
          afterTitle={isCarouselLayout && sessions.length > 0 ? (
            <AIGeneratingModalSessionsList
              listClassName={styles.carouselList}
              sessions={sessions}
              onSessionOpen={handleSessionOpen}
              onActiveSessionChange={setActiveSessionId}
              getSessionStatus={getSessionStatus}
              getSessionDisplayName={getSessionDisplayName}
              layout="carousel"
              activeSessionId={activeSessionId}
            />
          ) : null}
        />
        {isSessionsOverviewVisible ? (
          <AIGeneratingModalSessionsList
            listClassName={styles.list}
            sessions={sessions}
            onSessionOpen={handleSessionOpen}
            getSessionStatus={getSessionStatus}
            getSessionDisplayName={getSessionDisplayName}
          />
        ) : (
          <>
            {sessions.length > 0 && !isCarouselLayout ? (
              <button
                type="button"
                className={styles.backButton}
                onClick={handleBackToSessions}
                aria-label={t('common.back')}
              >
                <span>{t('common.back')}</span>
              </button>
            ) : null}
            <div className={styles.list} role="list">
              {currentSelectedCards.map((card, index) => {
                const item = items[index];
                if (!item) {
                  return null;
                }

                return (
                  <AIGeneratingModalListItem
                    key={item.nodeId}
                    item={item}
                    card={card}
                    onCardClick={handleCardClick}
                  />
                );
              })}
            </div>
          </>
        )}
        <Button
          title={t('valueChainMap.aiGeneratingModalContinue')}
          variant={BUTTON_VARIANT.primary}
          onClick={onClose}
          className={styles.continueButton}
        />
      </div>
    </Backdrop>,
    document.body,
  );
}

export default AIGeneratingModal;
